import React, { useEffect, useState } from 'react';
import KeywordCloud from '@/features/researcher/components/charts/KeywordCloud';
import ScholarOutput5YearChart from '@/features/researcher/components/charts/ScholarOutput5YearChart';
import CollaborationDonut from '@/features/researcher/components/charts/CollaborationDonut';
import MajorJournalsBar from '@/features/researcher/components/charts/MajorJournalsBar';
import MajorPartnersTreemap from '@/features/researcher/components/charts/MajorPartnersTreemap';
import { post } from '@/features/shared/lib/api';

/**
 * ResearcherMetrics
 * - Full Metrics tab content component.
 * - Props: active (boolean), className, record (researcher detail record), scholarId
 */
export default function ResearcherMetrics({ active, className = '', record, scholarId = null }) {
  // record에서 scholar_id 추출 (없으면 prop 사용)
  const id = scholarId || record?.scholar_id || record?.id || null;
  const base = `${active ? 'active' : 'hidden'} pt-[50px]  lg:pt-[60px]`;

  // 키워드 클라우드 데이터 상태
  const [keywordData, setKeywordData] = useState([]);
  const [loading, setLoading] = useState(false);
  const didFetchRef = React.useRef(false);

  // Derive metrics safely from API record
  const all = record?.research_all_achievement || {};
  const last5 = record?.research_last5year_achievement || {};

  const safeNum = (v) => (typeof v === 'number' ? v : Number(v) || 0);
  const avgCite = (cited, resources) => {
    const c = safeNum(cited);
    const r = safeNum(resources);
    if (!r) return 0;
    return Number((c / r).toFixed(1));
  };

  const curYY = String(new Date().getFullYear() % 100).padStart(2, '0');

  // 키워드 클라우드 API 호출
  useEffect(() => {
    if (!id || !active || didFetchRef.current) return; // active가 false거나 이미 가져왔으면 API 호출 안함

    const fetchKeywordCloud = async () => {
      try {
        setLoading(true);
        // POST 요청으로 cloudType과 query 전달
        const requestBody = {
          cloudType: 'scholar',
          query: {
            scholar_id: id,
          },
        };

        const response = await post('/rm/v1/stats/keyword_cloud', requestBody);

        // API 응답에서 records 추출
        let rawRecords = [];
        if (response?.result?.records && Array.isArray(response.result.records)) {
          rawRecords = response.result.records;
        } else if (response?.records && Array.isArray(response.records)) {
          rawRecords = response.records;
        } else if (Array.isArray(response)) {
          rawRecords = response;
        }

        const keywords = rawRecords.map((item) => ({
          keyword: item.text || item.keyword || item.label || '',
          count: item.size || item.count || item.value || 1,
        })).filter((item) => item.keyword); // 빈 키워드 제거

        setKeywordData(keywords);
        didFetchRef.current = true;
      } catch (error) {
        console.error('Error fetching keyword cloud:', error);
        setKeywordData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKeywordCloud();
  }, [id, active]);

  return (
    <div className={`${base} ${className}`.trim()}>
      <div>
        <h3 className="mb-4 text-[18px] font-bold leading-[100%]">Research Achievement</h3>

        <div className="overflow-x-auto">
          <table className="min-w-[800px]">
            <thead>
              <tr>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}></th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>Publications</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>Avg · Citation</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>Publications<br />In Top 10 Journal</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>Single</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>International</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>SCIs</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>Scopus</th>
                <th style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>KCI</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td className="" style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>All</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.resource_count)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.avg_citation)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.in_top10_journal)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.sole_author_count)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.international_count)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.SCI)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.Scopus)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(all.KCI)}</td>
              </tr>

              <tr>
                <td className="" style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>5 Year</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.resource_count)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.avg_citation)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.in_top10_journal)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.sole_author_count)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.international_count)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.SCI)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.Scopus)}</td>
                <td style={{ paddingRight: '2px', paddingLeft: '2px', width: 'auto' }}>{safeNum(last5.KCI)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {Array.isArray(keywordData) && keywordData.length > 0 && (
        <div className="mt-[50px] lg:mt-[60px]">
          <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Keyword Cloud
          </h3>
          <div className="relative p-[18px] border border-[#E4E8F1] rounded-[12px] lg:p-[22px]">
            <KeywordCloud items={keywordData} maxWordsCollapsed={100} maxWordsExpanded={0} />
          </div>
        </div>
      )}

      <div className="mt-[50px] lg:mt-[60px] 2xl:flex 2xl:items-stretch 2xl:gap-10">
        <div className="2xl:w-[calc((100%-40px)/2)]">
          <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">
            Annual Publication Output (Last 5 Years)
          </h3>
          <div className="p-[24px] border border-[#E4E8F1] rounded-[12px]">
            <ScholarOutput5YearChart scholarId={id} />
          </div>
        </div>

        <div className="mt-[50px] lg:mt-[60px] 2xl:mt-0 2xl:w-[calc((100%-40px)/2)]">
          <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">
            Collaboration
          </h3>
          <div className="h-[calc(100%-42px)] p-6 lg:py-10 border border-[#E4E8F1] rounded-[12px]">
            <CollaborationDonut scholarId={id} />
          </div>
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">
          List of Major Publication Journals
        </h3>
        <div className="p-[24px] border border-[#E4E8F1] rounded-[12px]">
          <MajorJournalsBar scholarId={id} />
        </div>
      </div>

      <div className="mt-[50px] lg:mt-[60px]">
        <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Major Partner Research Institutions</h3>
        <div className="">
          <MajorPartnersTreemap scholarId={id} />
        </div>
      </div>
    </div>
  );
}
